import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {IUserStore} from "data/stores/user/user.store";
import {Bindings} from "data/constants/bindings";
import {action, makeAutoObservable, observable, runInAction, reaction} from "mobx";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {SSO} from "data/utils/SSO";
import RallySSO from "data/utils/SSO/rallySSO";
import {AxiosError} from "axios";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";
import {IApiResponse} from "data/services/http";
import {useNavigate} from "react-router-dom";

interface IParams {
	navigate: ReturnType<typeof useNavigate>;
}
export interface ISessionController extends ViewController<IParams> {
	get isSessionChecked(): boolean;
}

@injectable()
export class SessionController implements ISessionController {
	@observable _isSessionChecked = false;
	@observable _isLoadingSSO = true;
	@observable _navigate?: IParams["navigate"];
	@observable _disposers: (() => void)[] = [];
	_sso = new SSO();

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get isSessionChecked(): boolean {
		return this._isSessionChecked;
	}

	init(params: IParams) {
		this._navigate = params.navigate;
		if (!this._userStore.isAuthorized) {
			this.initSSO();
		}

		this._disposers = [
			reaction(
				() => this._userStore.user,
				(user) => {
					if (user && !user.isTermsAccepted) {
						this._modalsStore.showModal(ModalType.ACCEPT_TERMS);
						this._navigate?.("/");
					}
				}
			),
		];
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
	}

	@action
	private initSSO() {
		if (RallySSO.IS_NATIVE) {
			void this._sso.initRallySSO().then(this._onSuccess);
		} else if (SSO.IS_BACKDOOR_MODE) {
			SSO.keepBackdoorSession();
			void this._userStore
				.requestUser()
				.finally(() => {
					runInAction(() => {
						this._isSessionChecked = true;
						sessionStorage.setItem("backdoor", "true");
					});
				})
				.catch(this.onError);
			return;
		} else {
			void this._sso.initWebAuthSSO().then(this._onSuccess);
		}
	}

	private _onSuccess = async () => {
		const sso = this._sso.toJSON();
		this._userStore.saveSSO(sso);
		try {
			const result = await this._userStore.checkUser(sso.uid);
			if (!result.terms && result.isNewUser) {
				this._userStore.saveSSODataForRegistration({
					...sso,
					displayName: result.displayName || "",
				});
				this._navigate?.("/");
				return;
			}
			try {
				await this._userStore.login(this._sso.toJSON()).catch(() => {
					this._userStore.saveSSODataForRegistration(sso);
					this._navigate?.("/");
				});
			} catch (e) {
				this.onError(e as AxiosError<IApiResponse>);
			}
		} catch (e) {
			SSO.TO_UPDATE_FORM();
		} finally {
			runInAction(() => {
				this._isSessionChecked = true;
			});
		}
	};
	private onError = (e: AxiosError) => {
		if (e.response?.status !== 401) {
			this._modalsStore.showModal(ModalType.ERROR, {
				message: this._i18nStore.t(e.message, e.message),
			});
		}
	};
}
